import { ComponentType } from 'react';
import { useTranslation } from 'next-i18next';
import { PhoneIcon } from '@heroicons/react/24/outline';

/**
 * Zmeu's number. WhatsApp and the voice agent share it for now, so the same
 * digits appear in both a wa.me link and a tel: link.
 *
 * Hardcoded because zmeu is not deployed yet. Once it is, the WhatsApp entry
 * should point at its /start redirect instead, so the number can change without
 * a frontend release and the drop-off between pressing the link and actually
 * sending the message becomes measurable.
 */
export const ZMEU_PHONE_NUMBER = '40373808166';

/** Opens WhatsApp with the first message already typed; the user still sends it. */
export const ZMEU_WHATSAPP_LINK = `https://wa.me/${ZMEU_PHONE_NUMBER}?text=${encodeURIComponent('Vreau să adaug o lucrare')}`;

/** Dial intent — the phone places the call, we do not. */
export const ZMEU_CALL_LINK = `tel:+${ZMEU_PHONE_NUMBER}`;

export interface AddTaskItem {
  href: string;
  icon: ComponentType<{ className?: string }>;
  /** Row tooltip — the visible text is just "Task", so this says which way in it is. */
  ariaLabel: string;
  dataTestId: string;
  external: boolean;
}

/**
 * The ways into zmeu, rendered as a "Task" row per entry in the desktop navbar menu
 * and the mobile bottom bar — shared so the links and the wording cannot drift apart.
 *
 * Only the call is offered for now. The WhatsApp entry below is ready to go back in
 * once that side is manned — it needs WhatsAppIcon from @components/common/Icons/Brands
 * imported again; ZMEU_WHATSAPP_LINK stays exported meanwhile.
 *
 *     {
 *       href: ZMEU_WHATSAPP_LINK,
 *       icon: WhatsAppIcon,
 *       ariaLabel: `${t('add.task')} (WhatsApp)`,
 *       dataTestId: 'nav-add-task-whatsapp',
 *       external: true,
 *     },
 */
export const useAddTaskItems = (): AddTaskItem[] => {
  const { t } = useTranslation('common');

  return [
    {
      href: ZMEU_CALL_LINK,
      icon: PhoneIcon,
      ariaLabel: `${t('add.task')} (${t('recruiter-call.price.free')})`,
      dataTestId: 'nav-add-task-call',
      // tel: must not open a new tab — the handset takes over instead.
      external: false,
    },
  ];
};
